'use client';

import {
  Box,
  Container,
  Typography,
  Paper,
  Divider,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Button,
  Alert,
  useTheme,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Gavel as GavelIcon,
  Person as PersonIcon,
  Article as ArticleIcon,
  AutoAwesome as AIIcon,
  Payment as PaymentIcon,
  Security as SecurityIcon,
  Block as BlockIcon,
  Warning as WarningIcon,
  Email as EmailIcon,
} from '@mui/icons-material';
import Link from 'next/link';
import { AppHeader } from '@/components/layout/AppHeader';

const LAST_UPDATED = 'January 27, 2026';
const COMPANY_NAME = 'Mengir';
const COMPANY_EMAIL = 'legal@mengir.me';

export default function TermsOfServicePage() {
  const theme = useTheme();

  const sections = [
    {
      id: 'acceptance',
      title: '1. Acceptance of Terms',
      icon: <GavelIcon />,
      content: (
        <>
          <Typography variant="body1" sx={{ mb: 2 }}>
            By accessing or using {COMPANY_NAME} (&quot;the Platform&quot;), you agree to be bound by these Terms of Service
            (&quot;Terms&quot;). If you do not agree to these Terms, you may not use the Platform.
          </Typography>
          <Typography variant="body1">
            These Terms constitute a legally binding agreement between you and {COMPANY_NAME}. We may modify these
            Terms at any time. Continued use after changes constitutes acceptance of the modified Terms.
          </Typography>
        </>
      ),
    },
    {
      id: 'eligibility',
      title: '2. Eligibility and Accounts',
      icon: <PersonIcon />,
      content: (
        <>
          <Typography variant="h6" sx={{ fontWeight: 600, mt: 2, mb: 1 }}>
            2.1 Eligibility
          </Typography>
          <List dense>
            <ListItem>
              <ListItemText primary="You must be at least 18 years old to use the Platform." />
            </ListItem>
            <ListItem>
              <ListItemText primary="You must provide accurate, complete, and current information." />
            </ListItem>
            <ListItem>
              <ListItemText primary="You are responsible for maintaining the confidentiality of your account credentials." />
            </ListItem>
          </List>

          <Typography variant="h6" sx={{ fontWeight: 600, mt: 3, mb: 1 }}>
            2.2 Account Responsibilities
          </Typography>
          <List dense>
            <ListItem>
              <ListItemText primary="You are responsible for all activities under your account." />
            </ListItem>
            <ListItem>
              <ListItemText primary="Notify us immediately of any unauthorized access or security breach." />
            </ListItem>
            <ListItem>
              <ListItemText primary="One account per person; multiple accounts may be terminated." />
            </ListItem>
            <ListItem>
              <ListItemText primary="You may not transfer or sell your account to another party." />
            </ListItem>
          </List>
        </>
      ),
    },
    {
      id: 'user-content',
      title: '3. User Content and Conduct',
      icon: <ArticleIcon />,
      content: (
        <>
          <Typography variant="h6" sx={{ fontWeight: 600, mt: 2, mb: 1 }}>
            3.1 Your Content
          </Typography>
          <Typography variant="body1" sx={{ mb: 2 }}>
            You retain ownership of content you create (listings, events, posts, messages). By posting content,
            you grant {COMPANY_NAME} a non-exclusive, worldwide, royalty-free license to use, display, and
            distribute your content on the Platform.
          </Typography>

          <Typography variant="h6" sx={{ fontWeight: 600, mt: 3, mb: 1 }}>
            3.2 Content Standards
          </Typography>
          <Typography variant="body1" sx={{ mb: 1 }}>
            You agree not to post content that:
          </Typography>
          <List dense>
            <ListItem>
              <ListItemText primary="Is false, misleading, or deceptive" />
            </ListItem>
            <ListItem>
              <ListItemText primary="Infringes intellectual property rights of others" />
            </ListItem>
            <ListItem>
              <ListItemText primary="Contains hate speech, harassment, or discrimination" />
            </ListItem>
            <ListItem>
              <ListItemText primary="Promotes illegal activities or violence" />
            </ListItem>
            <ListItem>
              <ListItemText primary="Contains malware, spam, or phishing attempts" />
            </ListItem>
            <ListItem>
              <ListItemText primary="Violates any applicable laws or regulations" />
            </ListItem>
          </List>

          <Typography variant="h6" sx={{ fontWeight: 600, mt: 3, mb: 1 }}>
            3.3 Listing Accuracy
          </Typography>
          <Typography variant="body1">
            You are solely responsible for the accuracy of your listings, including pricing, availability,
            descriptions, and images. Misrepresentation may result in account suspension or termination.
          </Typography>
        </>
      ),
    },
    {
      id: 'ai-features',
      title: '4. AI-Generated Content',
      icon: <AIIcon />,
      content: (
        <>
          <Alert severity="info" sx={{ mb: 2 }}>
            {COMPANY_NAME} offers AI-powered content generation features to help create listings and events.
          </Alert>

          <Typography variant="h6" sx={{ fontWeight: 600, mt: 2, mb: 1 }}>
            4.1 AI Content Policies
          </Typography>
          <List dense>
            <ListItem>
              <ListItemText
                primary="Human Review Required"
                secondary="You must review and verify all AI-generated content before publishing. You are responsible for its accuracy."
              />
            </ListItem>
            <ListItem>
              <ListItemText
                primary="Content Ownership"
                secondary="AI-generated content created using your inputs belongs to you, subject to these Terms."
              />
            </ListItem>
            <ListItem>
              <ListItemText
                primary="No Guarantees"
                secondary="AI-generated content is provided 'as is' without warranties of accuracy or fitness for any purpose."
              />
            </ListItem>
            <ListItem>
              <ListItemText
                primary="Usage Quotas"
                secondary="AI generation features are subject to usage limits based on your subscription tier."
              />
            </ListItem>
          </List>

          <Typography variant="h6" sx={{ fontWeight: 600, mt: 3, mb: 1 }}>
            4.2 AI Usage Restrictions
          </Typography>
          <Typography variant="body1">
            You may not use AI features to generate content that violates these Terms, infringes third-party rights,
            or attempts to deceive users. AI-generated content must comply with all content standards.
          </Typography>
        </>
      ),
    },
    {
      id: 'payments',
      title: '5. Payments and Subscriptions',
      icon: <PaymentIcon />,
      content: (
        <>
          <Typography variant="h6" sx={{ fontWeight: 600, mt: 2, mb: 1 }}>
            5.1 Subscription Plans
          </Typography>
          <List dense>
            <ListItem>
              <ListItemText
                primary="Billing"
                secondary="Paid subscriptions are billed in advance on a monthly basis through Stripe."
              />
            </ListItem>
            <ListItem>
              <ListItemText
                primary="Auto-Renewal"
                secondary="Subscriptions automatically renew unless cancelled before the renewal date."
              />
            </ListItem>
            <ListItem>
              <ListItemText
                primary="Price Changes"
                secondary="We may change prices with 30 days notice. Changes apply at the next billing cycle."
              />
            </ListItem>
          </List>

          <Typography variant="h6" sx={{ fontWeight: 600, mt: 3, mb: 1 }}>
            5.2 Refund Policy
          </Typography>
          <List dense>
            <ListItem>
              <ListItemText
                primary="Subscription Refunds"
                secondary="Subscription fees are generally non-refundable. You may cancel at any time, but access continues until the end of the billing period."
              />
            </ListItem>
            <ListItem>
              <ListItemText
                primary="AI Generation Credits"
                secondary="Used AI generation credits are non-refundable. Unused credits do not carry over between billing periods."
              />
            </ListItem>
          </List>

          <Typography variant="h6" sx={{ fontWeight: 600, mt: 3, mb: 1 }}>
            5.3 Payment Processing
          </Typography>
          <Typography variant="body1">
            Payments are processed securely by Stripe. By making a purchase, you agree to
            Stripe&apos;s <Link href="https://stripe.com/legal" target="_blank" rel="noopener" style={{ color: theme.palette.primary.main }}>Terms of Service</Link>.
            We do not store complete payment card information.
          </Typography>
        </>
      ),
    },
    {
      id: 'prohibited',
      title: '6. Prohibited Activities',
      icon: <BlockIcon />,
      content: (
        <>
          <Typography variant="body1" sx={{ mb: 2 }}>
            The following activities are strictly prohibited:
          </Typography>
          <List dense>
            <ListItem>
              <ListItemText primary="Creating multiple accounts to circumvent restrictions or bans" />
            </ListItem>
            <ListItem>
              <ListItemText primary="Scraping, crawling, or automated data collection without permission" />
            </ListItem>
            <ListItem>
              <ListItemText primary="Interfering with or disrupting the Platform's operation" />
            </ListItem>
            <ListItem>
              <ListItemText primary="Attempting to gain unauthorized access to systems or accounts" />
            </ListItem>
            <ListItem>
              <ListItemText primary="Using the Platform for money laundering or fraudulent transactions" />
            </ListItem>
            <ListItem>
              <ListItemText primary="Impersonating others or misrepresenting your affiliation" />
            </ListItem>
            <ListItem>
              <ListItemText primary="Circumventing payment systems or manipulating AI usage quotas" />
            </ListItem>
            <ListItem>
              <ListItemText primary="Posting listings for illegal goods, services, or activities" />
            </ListItem>
          </List>
        </>
      ),
    },
    {
      id: 'termination',
      title: '7. Termination',
      icon: <WarningIcon />,
      content: (
        <>
          <Typography variant="h6" sx={{ fontWeight: 600, mt: 2, mb: 1 }}>
            7.1 By You
          </Typography>
          <Typography variant="body1" sx={{ mb: 2 }}>
            You may delete your account at any time through account settings. Upon deletion, your public
            content may be removed, but some information may be retained for legal or operational purposes.
          </Typography>

          <Typography variant="h6" sx={{ fontWeight: 600, mt: 3, mb: 1 }}>
            7.2 By Us
          </Typography>
          <Typography variant="body1" sx={{ mb: 2 }}>
            We may suspend or terminate your account at any time for:
          </Typography>
          <List dense>
            <ListItem>
              <ListItemText primary="Violation of these Terms" />
            </ListItem>
            <ListItem>
              <ListItemText primary="Fraudulent, illegal, or harmful activity" />
            </ListItem>
            <ListItem>
              <ListItemText primary="Extended account inactivity (12+ months)" />
            </ListItem>
            <ListItem>
              <ListItemText primary="Any reason with 30 days notice for accounts in good standing" />
            </ListItem>
          </List>
        </>
      ),
    },
    {
      id: 'disclaimers',
      title: '8. Disclaimers and Limitations',
      icon: <SecurityIcon />,
      content: (
        <>
          <Typography variant="h6" sx={{ fontWeight: 600, mt: 2, mb: 1 }}>
            8.1 Platform Disclaimer
          </Typography>
          <Typography variant="body1" sx={{ mb: 2 }}>
            THE PLATFORM IS PROVIDED &quot;AS IS&quot; AND &quot;AS AVAILABLE&quot; WITHOUT WARRANTIES OF ANY KIND,
            EXPRESS OR IMPLIED. WE DO NOT GUARANTEE UNINTERRUPTED, SECURE, OR ERROR-FREE OPERATION.
          </Typography>

          <Typography variant="h6" sx={{ fontWeight: 600, mt: 3, mb: 1 }}>
            8.2 User Interactions
          </Typography>
          <Typography variant="body1" sx={{ mb: 2 }}>
            {COMPANY_NAME} is a platform connecting users. We are not responsible for:
          </Typography>
          <List dense>
            <ListItem>
              <ListItemText primary="The accuracy or quality of user-generated content or listings" />
            </ListItem>
            <ListItem>
              <ListItemText primary="Transactions, agreements, or disputes between users" />
            </ListItem>
            <ListItem>
              <ListItemText primary="The conduct or reliability of other users" />
            </ListItem>
            <ListItem>
              <ListItemText primary="Any damages arising from user interactions" />
            </ListItem>
          </List>

          <Typography variant="h6" sx={{ fontWeight: 600, mt: 3, mb: 1 }}>
            8.3 Limitation of Liability
          </Typography>
          <Typography variant="body1">
            TO THE MAXIMUM EXTENT PERMITTED BY LAW, {COMPANY_NAME.toUpperCase()} SHALL NOT BE LIABLE FOR ANY INDIRECT,
            INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES, OR ANY LOSS OF PROFITS OR REVENUES,
            WHETHER INCURRED DIRECTLY OR INDIRECTLY. OUR TOTAL LIABILITY SHALL NOT EXCEED THE AMOUNT YOU
            PAID US IN THE 12 MONTHS PRECEDING THE CLAIM.
          </Typography>
        </>
      ),
    },
    {
      id: 'disputes',
      title: '9. Dispute Resolution',
      icon: <GavelIcon />,
      content: (
        <>
          <Typography variant="h6" sx={{ fontWeight: 600, mt: 2, mb: 1 }}>
            9.1 Informal Resolution
          </Typography>
          <Typography variant="body1" sx={{ mb: 2 }}>
            Before filing any legal claim, you agree to contact us at{' '}
            <Link href={`mailto:${COMPANY_EMAIL}`} style={{ color: theme.palette.primary.main }}>
              {COMPANY_EMAIL}
            </Link>{' '}
            and attempt to resolve the dispute informally for at least 30 days.
          </Typography>

          <Typography variant="h6" sx={{ fontWeight: 600, mt: 3, mb: 1 }}>
            9.2 Arbitration Agreement
          </Typography>
          <Typography variant="body1" sx={{ mb: 2 }}>
            Any disputes not resolved informally shall be resolved through binding arbitration under the
            rules of the American Arbitration Association. Arbitration shall be conducted in English,
            and the arbitrator&apos;s decision shall be final and binding.
          </Typography>

          <Typography variant="h6" sx={{ fontWeight: 600, mt: 3, mb: 1 }}>
            9.3 Class Action Waiver
          </Typography>
          <Typography variant="body1">
            You agree to resolve disputes with us on an individual basis only. You waive the right to
            participate in class actions, class arbitrations, or representative actions.
          </Typography>
        </>
      ),
    },
    {
      id: 'general',
      title: '10. General Provisions',
      icon: <ArticleIcon />,
      content: (
        <>
          <List dense>
            <ListItem>
              <ListItemText
                primary="Governing Law"
                secondary="These Terms are governed by the laws of the State of Delaware, USA, without regard to conflict of law principles."
              />
            </ListItem>
            <ListItem>
              <ListItemText
                primary="Entire Agreement"
                secondary="These Terms, along with our Privacy Policy, constitute the entire agreement between you and Mengir."
              />
            </ListItem>
            <ListItem>
              <ListItemText
                primary="Severability"
                secondary="If any provision is found unenforceable, the remaining provisions remain in effect."
              />
            </ListItem>
            <ListItem>
              <ListItemText
                primary="No Waiver"
                secondary="Failure to enforce any right does not waive our ability to enforce it later."
              />
            </ListItem>
            <ListItem>
              <ListItemText
                primary="Assignment"
                secondary="We may assign these Terms; you may not assign without our consent."
              />
            </ListItem>
          </List>
        </>
      ),
    },
    {
      id: 'contact',
      title: '11. Contact Us',
      icon: <EmailIcon />,
      content: (
        <>
          <Typography variant="body1">
            For questions about these Terms, contact us:
          </Typography>
          <Box sx={{ mt: 2, p: 2, bgcolor: 'action.hover', borderRadius: 2 }}>
            <Typography variant="body1" sx={{ fontWeight: 600 }}>
              {COMPANY_NAME} Legal Team
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Email:{' '}
              <Link href={`mailto:${COMPANY_EMAIL}`} style={{ color: theme.palette.primary.main }}>
                {COMPANY_EMAIL}
              </Link>
            </Typography>
          </Box>
        </>
      ),
    },
  ];

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'background.default' }}>
      <AppHeader authenticated={false} />

      <Container maxWidth="md" sx={{ px: { xs: 2, sm: 3 }, py: { xs: 3, sm: 5 } }}>
        {/* Back Button */}
        <Button
          component={Link}
          href="/"
          startIcon={<ArrowBackIcon />}
          sx={{
            textTransform: 'none',
            mb: 3,
            minHeight: 44,
            fontWeight: 600,
            '&:active': { bgcolor: 'action.selected' },
          }}
        >
          Back
        </Button>

        {/* Header */}
        <Paper sx={{ p: { xs: 3, sm: 4 }, borderRadius: 3, mb: 3 }}>
          <Typography
            variant="h4"
            sx={{ fontWeight: 800, mb: 1, fontSize: { xs: '1.5rem', sm: '2rem' } }}
          >
            Terms of Service
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Last updated: {LAST_UPDATED}
          </Typography>
          <Typography variant="body1" sx={{ lineHeight: 1.7 }}>
            Welcome to {COMPANY_NAME}! These Terms of Service govern your use of our platform, which connects
            Ethiopian and Eritrean communities through business listings, property sales, events, and
            community features. Please read these Terms carefully before using our services.
          </Typography>
        </Paper>

        {/* Important Notice */}
        <Alert severity="warning" sx={{ mb: 3, borderRadius: 2 }}>
          <Typography variant="body2" sx={{ fontWeight: 600 }}>
            By using {COMPANY_NAME}, you agree to these Terms and our{' '}
            <Link href="/privacy" style={{ color: 'inherit', textDecoration: 'underline' }}>
              Privacy Policy
            </Link>
            . If you do not agree, please do not use the Platform.
          </Typography>
        </Alert>

        {/* Table of Contents */}
        <Paper sx={{ p: { xs: 2, sm: 3 }, borderRadius: 3, mb: 3 }}>
          <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
            Contents
          </Typography>
          <List dense disablePadding>
            {sections.map((section) => (
              <ListItem
                key={section.id}
                component={Link}
                href={`#${section.id}`}
                sx={{
                  py: 1,
                  borderRadius: 1,
                  '&:hover': { bgcolor: 'action.hover' },
                }}
              >
                <ListItemIcon sx={{ minWidth: 36 }}>{section.icon}</ListItemIcon>
                <ListItemText
                  primary={section.title}
                  primaryTypographyProps={{ fontSize: '0.95rem' }}
                />
              </ListItem>
            ))}
          </List>
        </Paper>

        {/* Sections */}
        {sections.map((section) => (
          <Paper
            key={section.id}
            id={section.id}
            sx={{ p: { xs: 3, sm: 4 }, borderRadius: 3, mb: 3 }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
              <Box sx={{ color: 'primary.main' }}>{section.icon}</Box>
              <Typography variant="h5" sx={{ fontWeight: 700, fontSize: { xs: '1.1rem', sm: '1.25rem' } }}>
                {section.title}
              </Typography>
            </Box>
            <Divider sx={{ mb: 2 }} />
            {section.content}
          </Paper>
        ))}

        {/* Changes Notice */}
        <Paper sx={{ p: { xs: 3, sm: 4 }, borderRadius: 3, bgcolor: 'action.hover' }}>
          <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
            Changes to These Terms
          </Typography>
          <Typography variant="body1">
            We may update these Terms periodically. Material changes will be communicated via email or
            a prominent notice on the Platform at least 30 days before taking effect. Your continued use
            after changes constitutes acceptance of the modified Terms.
          </Typography>
        </Paper>
      </Container>
    </Box>
  );
}
